import { cn } from '@rocco/utils/cn';

export interface SectionSpacerProps {
    /** Gap height in Figma px at the 1920 desktop frame; rendered in vw. */
    height: number;
    /**
     * Gap height in Figma px at the 414 mobile frame. Defaults to the desktop
     * value scaled proportionally when omitted. The mobile design uses its own
     * rhythm (e.g. a flat 59px between the top bands), independent of desktop.
     */
    mobileHeight?: number;
    /** Background colour class for the gap (defaults to transparent / page bg). */
    className?: string;
    /**
     * Desktop unit. `vw` (default) scales the gap with the viewport off the 1920
     * frame; `rem` holds the gap at the design px regardless of viewport (use on
     * pages whose content is rem-anchored so the rhythm matches at any width).
     */
    unit?: 'vw' | 'rem';
}

/**
 * Vertical gap between homepage sections, mirroring the spacer frames in the
 * Figma page. Desktop sizes off the 1920 frame, mobile off the 414 frame, each
 * in vw so the rhythm scales with the sections it separates. Rendered as two
 * breakpoint-scoped divs because the two frames need different vw bases.
 */
export const SectionSpacer = ({
    height,
    mobileHeight,
    className,
    unit = 'vw',
}: SectionSpacerProps) => {
    const mobileVw =
        mobileHeight != null
            ? (mobileHeight / 414) * 100
            : (height / 1920) * 100;
    const desktopHeight =
        unit === 'rem' ? `${height / 16}rem` : `${(height / 1920) * 100}vw`;
    return (
        <>
            <div
                aria-hidden="true"
                className={cn('w-full md:hidden', className)}
                style={{ height: `${mobileVw}vw` }}
            />
            <div
                aria-hidden="true"
                className={cn('hidden w-full md:block', className)}
                style={{ height: desktopHeight }}
            />
        </>
    );
};
